"use client";

// GM attention queue — GET /api/admin/dashboard (`operationalAlerts`).
// High-priority buckets only: customers leaving and stalled negotiations.

import { adminApi, type AdminDashboard } from "../lib/api";
import { toAlertRows, type AlertRow } from "../lib/adminMap";
import { useApi } from "../lib/useApi";
import Icon, { type IconName } from "./Icon";
import { Skeleton } from "./Skeleton";
import { PanelEmpty, PanelError } from "./PanelState";

const typeIcon: Record<string, IconName> = {
  "Customer Leaving": "leaving",
  "High Bell Count": "bell",
};

const severityColor: Record<AlertRow["severity"], string> = {
  critical: "var(--danger)",
  warning: "var(--warn)",
};

export default function OperationalAlerts() {
  const { data, loading, error, reload } = useApi<AdminDashboard>(
    (signal) => adminApi.dashboard({}, signal),
    [],
    "Failed to load alerts.",
  );

  const rows = toAlertRows(data?.operationalAlerts);
  const critical = rows.filter((a) => a.severity === "critical").length;
  const bellsUnavailable =
    data?.operationalAlerts?.highBellCount?.available === false
      ? data.operationalAlerts.highBellCount.reason
      : undefined;

  return (
    <div className="glass rounded-[26px] p-5 sm:p-6 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span
            className="live-dot w-2.5 h-2.5 rounded-full"
            style={{ background: "var(--danger)" }}
          />
          <h2 className="font-display text-lg text-text">Operational Alerts</h2>
        </div>
        {/* "0 critical" during load would read as all-clear. */}
        {loading ? (
          <Skeleton className="h-3.5 w-16 rounded" />
        ) : (
          <span className="text-[12px] text-text-faint">{critical} critical</span>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-2xl" />
          ))}
        </div>
      ) : error ? (
        <PanelError message={error} onRetry={reload} />
      ) : rows.length === 0 ? (
        <PanelEmpty
          title="No active alerts"
          hint={
            bellsUnavailable ??
            "Escalations and walkouts surface here in real time."
          }
          icon="shield"
        />
      ) : (
        <ul
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-label="Operational alerts"
          className="flex flex-col gap-2.5 overflow-y-auto -mr-2 pr-2"
        >
          {rows.map((a) => {
            const isCritical = a.severity === "critical";
            return (
              <li
                key={a.id}
                className={`flex items-center gap-3 px-3.5 py-3 rounded-2xl ${isCritical ? "tint" : ""}`}
                style={
                  isCritical
                    ? ({ "--tint-color": "var(--rose)" } as React.CSSProperties)
                    : undefined
                }
              >
                <span
                  className="chip grid place-items-center w-9 h-9 rounded-xl shrink-0"
                  style={
                    {
                      color: severityColor[a.severity],
                      "--tint-color": isCritical
                        ? "var(--rose)"
                        : "var(--peach)",
                    } as React.CSSProperties
                  }
                >
                  <Icon name={typeIcon[a.type] ?? "bell"} size={17} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-semibold text-text truncate">
                    {a.type}
                  </p>
                  <p className="text-[12px] text-text-faint truncate">
                    {a.message}
                  </p>
                </div>
                <div className="text-right shrink-0 flex items-center gap-2.5">
                  <span
                    className="chip px-2 py-0.5 rounded-full text-[11px] font-semibold"
                    style={
                      {
                        color: severityColor[a.severity],
                        "--tint-color": isCritical
                          ? "var(--rose)"
                          : "var(--peach)",
                      } as React.CSSProperties
                    }
                  >
                    ×{a.count}
                  </span>
                  <span className="text-[11px] text-text-faint tabular-nums">
                    {a.timestamp}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
